import { useState } from 'react';
import { useDropzone } from 'react-dropzone'; // Note: Need to install react-dropzone if not present, otherwise use simple input
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

// Simple implementation without standard dropzone lib to avoid extra dep if not installed
// But prompt implies "Drag & drop". I'll use standard input with drag handlers if needed, 
// or I'll just use a file input styled as a drop area.

export default function ImageUpload({ value, onChange }) {
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const MAX_BYTES = 5 * 1024 * 1024;
    const ACCEPTED_LABEL =
        'JPEG, JPG, PNG, WebP, GIF — taille max. 5 Mo';

    const uploadImage = async (file) => {
        try {
            if (!file.type.startsWith('image/')) {
                alert(`Fichier non reconnu comme image.\n\nFormats acceptés : ${ACCEPTED_LABEL}`);
                return;
            }
            if (file.size > MAX_BYTES) {
                alert("L'image dépasse 5 Mo. Réduisez la taille ou compressez le fichier.");
                return;
            }
            setUploading(true);
            const fileExt = file.name.split('.').pop();
            const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
            const filePath = `${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('events')
                .upload(filePath, file);

            if (uploadError) {
                throw uploadError;
            }

            // Get public URL
            const { data: { publicUrl } } = supabase.storage
                .from('events')
                .getPublicUrl(filePath);

            onChange(publicUrl);
        } catch (error) {
            console.error('Error uploading image:', error);
            alert(
                `Erreur lors du téléchargement.\n\nFormats acceptés : ${ACCEPTED_LABEL}`
            );
        } finally {
            setUploading(false);
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            uploadImage(e.dataTransfer.files[0]);
        }
    };

    const handleChange = (e) => {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            uploadImage(e.target.files[0]);
        }
    };

    return (
        <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">Image de couverture</label>
            <p className="text-xs text-gray-600 -mt-1 mb-1">
                Formats acceptés : JPEG, JPG, PNG, WebP, GIF · taille max. 5 Mo
            </p>

            {value ? (
                <div className="relative aspect-video w-full rounded-lg overflow-hidden border border-gray-200 group">
                    <img src={value} alt="Cover" className="w-full h-full object-cover" />
                    <button
                        type="button"
                        onClick={() => onChange('')}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                        <X size={16} />
                    </button>
                </div>
            ) : (
                <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    className={cn(
                        "relative border-2 border-dashed rounded-lg p-12 text-center transition-colors cursor-pointer",
                        dragActive ? "border-primary-500 bg-primary-50" : "border-gray-300 hover:border-primary-400"
                    )}
                >
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="flex flex-col items-center gap-2">
                        {uploading ? (
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                        ) : (
                            <>
                                <div className="p-4 bg-primary-50 rounded-full text-primary-600">
                                    <Upload size={24} />
                                </div>
                                <p className="text-sm font-medium text-gray-900">
                                    Cliquez ou glissez une image ici
                                </p>
                                <p className="text-xs text-gray-500">
                                    {ACCEPTED_LABEL}
                                </p>
                            </>
                        )}
                    </div>
                </div>
            )}

            <p className="text-xs text-gray-500">
                Si aucune image n&apos;est fournie, une image par défaut sera utilisée selon le type d&apos;événement.
            </p>
        </div>
    );
}
